import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import '../../styles/CreateAssignmentPage.css';

const LearningPlansListPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch plans on mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/learning-plans/teacher');
      setPlans(response.data.data || []);
    } catch (error) {
      console.error('Error fetching learning plans:', error);
      setError('Failed to load learning plans. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Filter plans by status
  const filteredPlans = plans.filter(plan => {
    if (statusFilter === 'all') return true;
    return plan.status === statusFilter;
  });

  // Get goal display text
  const getGoalDisplay = (plan) => {
    const { goal } = plan;
    if (goal.type === 'pages') {
      return `Pages ${goal.pages.from}-${goal.pages.to}`;
    } else if (goal.type === 'verses') {
      return `${goal.verses.total} verses`;
    } else if (goal.type === 'juz') {
      return `Juz ${goal.juz.number}`;
    } else if (goal.type === 'surahs') {
      return `${goal.surahs.length} surahs`;
    } else if (goal.type === 'lines') {
      return `~${goal.lines.dailyLines} lines per day`;
    }
    return 'Unknown goal';
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return '#4caf50';
      case 'paused':
        return '#ff9800';
      case 'completed':
        return '#2196f3';
      case 'cancelled':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  // Get progress bar color
  const getProgressColor = (percent) => {
    if (percent >= 75) return '#4caf50';
    if (percent >= 50) return '#2196f3';
    if (percent >= 25) return '#ff9800';
    return '#f44336';
  };

  // Get schedule status
  // progress.due now represents OVERDUE assignments (deadline passed, not completed)
  const getScheduleStatus = (plan) => {
    const overdue = plan.progress?.due || 0;

    // No overdue assignments = on track
    if (overdue === 0) return { text: 'On Track', icon: '✓', color: '#4caf50' };

    // Has overdue assignments
    if (overdue >= 3) return { text: 'Behind Schedule', icon: '⚠', color: '#f44336' };
    return { text: 'Slightly Behind', icon: '⚠', color: '#ff9800' };
  };

  if (loading) {
    return (
      <div className="create-assignment-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Loading learning plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-assignment-container">
      <div className="header">
        <h1>Learning Plans</h1>
        <Link to="/dashboard" className="back-btn">
          ← Back to Dashboard
        </Link>
      </div>

      {error && (
        <div className="error-message" style={{marginBottom: '20px', padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '8px'}}>
          {error}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{marginBottom: '20px'}}>
        <button
          onClick={() => navigate('/learning-plans/new')}
          className="btn-primary"
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            width: '100%',
            maxWidth: '300px'
          }}
        >
          + Create New Learning Plan
        </button>
      </div>

      {/* Status Filter */}
      <div className="form-card" style={{marginBottom: '20px'}}>
        <div className="form-section">
          <div className="button-group">
            <button
              type="button"
              className={`option-btn ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({plans.length})
            </button>
            <button
              type="button"
              className={`option-btn ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Active ({plans.filter(p => p.status === 'active').length})
            </button>
            <button
              type="button"
              className={`option-btn ${statusFilter === 'paused' ? 'active' : ''}`}
              onClick={() => setStatusFilter('paused')}
            >
              Paused ({plans.filter(p => p.status === 'paused').length})
            </button>
            <button
              type="button"
              className={`option-btn ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('completed')}
            >
              Completed ({plans.filter(p => p.status === 'completed').length})
            </button>
          </div>
        </div>
      </div>

      {/* Plans List */}
      {filteredPlans.length === 0 ? (
        <div className="form-card">
          <div className="form-section" style={{textAlign: 'center', padding: '60px 20px'}}>
            <div style={{fontSize: '48px', marginBottom: '20px'}}>📚</div>
            <h3 style={{marginBottom: '10px', color: '#333'}}>
              {statusFilter === 'all' ? 'No Learning Plans Yet' : `No ${statusFilter} plans`}
            </h3>
            <p style={{color: '#666', marginBottom: '20px'}}>
              {statusFilter === 'all'
                ? 'Create your first learning plan to help students track their Quran memorization journey.'
                : `No plans with status "${statusFilter}"`
              }
            </p>
            {statusFilter === 'all' && (
              <button
                onClick={() => navigate('/learning-plans/new')}
                className="btn-primary"
                style={{padding: '12px 24px'}}
              >
                + Create Your First Plan
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
          {filteredPlans.map(plan => (
            <div
              key={plan._id}
              className="form-card"
              style={{cursor: 'pointer', transition: 'box-shadow 0.2s'}}
              onClick={() => navigate(`/learning-plans/${plan._id}`)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              <div className="form-section">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px'}}>
                  <div style={{flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px'}}>
                      <h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>
                        {plan.studentId?.name || 'Student'}
                      </h3>
                      <span
                        style={{
                          padding: '4px 12px',
                          backgroundColor: getStatusColor(plan.status),
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}
                      >
                        {plan.status}
                      </span>
                    </div>
                    <div style={{fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span>{getGoalDisplay(plan)}</span>
                      <span style={{color: '#999'}}>•</span>
                      <span style={{
                        fontSize: '13px',
                        color: '#2196f3',
                        textTransform: 'capitalize',
                        fontWeight: '500'
                      }}>
                        {plan.assignmentType ? plan.assignmentType.replace('-', ' ') : 'New Memorization'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Progress Section */}
                <div style={{marginBottom: '15px'}}>
                  {/* Completion Progress */}
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                    <span style={{fontSize: '13px', color: '#666'}}>
                      Completion: {plan.progress?.completed || 0} of {plan.progress?.total || 0} assignments
                    </span>
                    <span style={{fontSize: '14px', fontWeight: '500', color: getProgressColor(plan.progress?.percent || 0)}}>
                      {Math.round(plan.progress?.percent || 0)}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: `${plan.progress?.percent || 0}%`,
                      height: '100%',
                      backgroundColor: getProgressColor(plan.progress?.percent || 0),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>

                  {/* Schedule Status */}
                  {(() => {
                    const scheduleStatus = getScheduleStatus(plan);
                    const overdueCount = plan.progress?.due || 0;
                    return (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        color: scheduleStatus.color,
                        fontWeight: '500'
                      }}>
                        {scheduleStatus.icon && <span>{scheduleStatus.icon}</span>}
                        <span>Schedule: {scheduleStatus.text}</span>
                        {overdueCount > 0 && (
                          <span style={{color: '#666', fontWeight: '400'}}>
                            ({overdueCount} overdue)
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Timeline Info */}
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', fontSize: '13px'}}>
                  <div>
                    <div style={{color: '#999', marginBottom: '3px'}}>Start</div>
                    <div style={{color: '#333', fontWeight: '500'}}>{formatDate(plan.timeline.start)}</div>
                  </div>
                  <div>
                    <div style={{color: '#999', marginBottom: '3px'}}>End</div>
                    <div style={{color: '#333', fontWeight: '500'}}>{formatDate(plan.timeline.end)}</div>
                  </div>
                  <div>
                    <div style={{color: '#999', marginBottom: '3px'}}>Schedule</div>
                    <div style={{color: '#333', fontWeight: '500'}}>{plan.schedule.daysPerWeek} days/week</div>
                  </div>
                </div>

                {/* Notes Preview */}
                {plan.notes && (
                  <div style={{
                    marginTop: '15px',
                    padding: '10px',
                    backgroundColor: '#f5f7fa',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    "{plan.notes.length > 100 ? plan.notes.substring(0, 100) + '...' : plan.notes}"
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Helper Text */}
      {filteredPlans.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#1565c0'
        }}>
          <strong>💡 Tip:</strong> Learning plans automatically track progress as you assess assignments. Click on any plan to view details or make changes.
        </div>
      )}
    </div>
  );
};

export default LearningPlansListPage;
